// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, firebaseEnabled } from '../services/firebase';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe usarse dentro de AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const saved = localStorage.getItem('hoommyUser');
        if (!saved) return null;
        const savedUser = JSON.parse(saved);
        const token = localStorage.getItem('hoommyToken');
        if (savedUser?.source === 'backend' && !token) {
            return null;
        }
        return savedUser;
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const saved = localStorage.getItem('hoommyUser');
        const token = localStorage.getItem('hoommyToken');
        if (!saved) return;
        const savedUser = JSON.parse(saved);
        if (savedUser?.source === 'backend' && !token) {
            localStorage.removeItem('hoommyUser');
            setUser(null);
        }
    }, []);

    useEffect(() => {
        // Si Firebase no está disponible, solo usar auth del backend
        if (!firebaseEnabled || !auth) {
            setLoading(false);
            return;
        }

        const { onAuthStateChanged } = require('firebase/auth');
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            if (firebaseUser) {
                const userData = {
                    id: firebaseUser.uid,
                    displayName: firebaseUser.displayName,
                    email: firebaseUser.email,
                    photoURL: firebaseUser.photoURL,
                    rol: 'arrendatario',
                    nombre_empresa: null,
                    source: 'firebase',
                };
                setUser(userData);
                localStorage.setItem('hoommyUser', JSON.stringify(userData));
            } else {
                const saved = localStorage.getItem('hoommyUser');
                const savedUser = saved ? JSON.parse(saved) : null;
                if (savedUser?.source === 'firebase') {
                    setUser(null);
                    localStorage.removeItem('hoommyUser');
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const loginWithBackend = (apiUser, token = null) => {
        const userData = {
            id: apiUser.id,
            displayName: apiUser.nombre,
            email: apiUser.email,
            photoURL: null,
            rol: apiUser.rol || 'arrendatario',
            nombre_empresa: apiUser.nombre_empresa || null,
            es_estudiante_verificado: apiUser.es_estudiante_verificado || false,
            source: 'backend',
        };
        setUser(userData);
        localStorage.setItem('hoommyUser', JSON.stringify(userData));
        if (token) {
            localStorage.setItem('hoommyToken', token);
        }
    };

    const refreshUser = async () => {
        const saved = localStorage.getItem('hoommyUser');
        const savedUser = saved ? JSON.parse(saved) : null;
        if (!savedUser?.id || savedUser.source !== 'backend') return;
        try {
            const API_URL = process.env.REACT_APP_API_URL || '';
            const res = await fetch(`${API_URL}/api/user/${savedUser.id}`);
            if (!res.ok) return;
            const freshData = await res.json();
            const userData = {
                ...savedUser,
                displayName: freshData.nombre,
                rol: freshData.rol,
                nombre_empresa: freshData.nombre_empresa || null,
                es_estudiante_verificado: freshData.es_estudiante_verificado || false,
            };
            setUser(userData);
            localStorage.setItem('hoommyUser', JSON.stringify(userData));
        } catch (e) {
            // silently fail
        }
    };

    const logout = async () => {
        if (firebaseEnabled && auth) {
            try {
                const { signOut } = require('firebase/auth');
                await signOut(auth);
            } catch (e) {
                // Ignore Firebase signout errors
            }
        }
        setUser(null);
        localStorage.removeItem('hoommyUser');
        localStorage.removeItem('hoommyToken');
    };

    const value = {
        user,
        loading,
        logout,
        loginWithBackend,
        refreshUser,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
